import { useState } from 'react'
import { C, R, F } from '../../lib/designTokens'
import PageHeader from '../../components/PageHeader'
import { useAsyncData } from './useProductKnowledgeData'
import * as pk from '../../services/productKnowledgeService'
import ProductKnowledgeUnitReader from './ProductKnowledgeUnitReader'
import ProductKnowledgeWorkflowReader from './ProductKnowledgeWorkflowReader'
import ProductKnowledgeSearch from './ProductKnowledgeSearch'
import ProductKnowledgeItemReader from './ProductKnowledgeItemReader'
import ProductKnowledgeGlossary from './ProductKnowledgeGlossary'

// ─────────────────────────────────────────────────────────────────────────
// Product Knowledge -- standalone reader experience.
//
// Distinct from the existing ContextualHelp drawer: this is a full page,
// its own browse hierarchy (Product -> Area -> Screen/Workflow), and its
// own search -- backed entirely by the catalog_* reader RPCs. No catalog
// content is hard-coded; every label/name shown here comes from the API.
// ─────────────────────────────────────────────────────────────────────────

const cardStyle = {
  background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.card,
  padding: '14px 16px', cursor: 'pointer', transition: 'all .12s',
}

function LoadingRow({ label }) {
  return <div style={{ padding: 24, textAlign: 'center', color: C.sub, fontSize: F.body }}>{label}</div>
}

function ErrorRow({ error, onRetry }) {
  return (
    <div style={{ padding: 20, textAlign: 'center' }}>
      <p style={{ color: C.red, fontSize: F.body, marginBottom: 10 }}>
        Could not load Product Knowledge content. / تعذّر تحميل محتوى قاعدة المعرفة.
      </p>
      <button onClick={onRetry} style={{ fontSize: F.caption, padding: '6px 14px', borderRadius: R.btn, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer' }}>
        Retry / إعادة المحاولة
      </button>
    </div>
  )
}

function AreaList({ areas, activeKey, onSelect }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {areas.map(a => (
        <button key={a.area_key} onClick={() => onSelect(a.area_key)}
          style={{
            ...cardStyle, textAlign: 'left', width: '100%',
            borderColor: activeKey === a.area_key ? C.blue : C.border,
            background: activeKey === a.area_key ? C.blueBg : C.surface,
          }}>
          <div style={{ fontSize: F.bodyLg, fontWeight: 700, color: C.ink }}>{a.area_key}</div>
        </button>
      ))}
    </div>
  )
}

function AreaContents({ areaKey, onOpenScreen, onOpenWorkflow }) {
  const { data, loading, error, reload } = useAsyncData(() => pk.browseArea(areaKey), [areaKey])

  if (loading) return <LoadingRow label="Loading area… / جارٍ التحميل…" />
  if (error) return <ErrorRow error={error} onRetry={reload} />
  if (!data?.found) return <LoadingRow label="This area is not available. / هذه المنطقة غير متاحة." />

  const { screens = [], workflows = [] } = data
  if (screens.length === 0 && workflows.length === 0) {
    return <LoadingRow label="No Product Knowledge content in this area yet. / لا يوجد محتوى بعد في هذه المنطقة." />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {workflows.length > 0 && (
        <div>
          <div style={{ fontSize: F.label, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Workflows / سير العمل
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {workflows.map(w => (
              <div key={w.key} onClick={() => onOpenWorkflow(w.key)} style={cardStyle}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.blue}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                <div style={{ fontSize: F.bodyLg, fontWeight: 700, color: C.ink }}>{w.name_en}</div>
                <div style={{ fontSize: F.body, color: C.sub, marginTop: 2 }} dir="rtl">{w.name_ar}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {screens.length > 0 && (
        <div>
          <div style={{ fontSize: F.label, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Screens / الشاشات
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {screens.map(s => (
              <div key={s.key} onClick={() => onOpenScreen(s.key)} style={cardStyle}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.blue}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                <div style={{ fontSize: F.bodyLg, fontWeight: 700, color: C.ink }}>{s.name_en}</div>
                <div style={{ fontSize: F.body, color: C.sub, marginTop: 2 }} dir="rtl">{s.name_ar}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// view: { mode: 'browse' | 'search' | 'glossary' | 'unit' | 'workflow' | 'item', ...payload }
export default function ProductKnowledge({ initialContext, onInitialContextConsumed }) {
  const [view, setView] = useState(initialContext || { mode: 'browse' })

  // initialContext is a one-shot instruction from a contextual link
  // elsewhere in CostSpace (see App.jsx's pendingPKContext). Consume it
  // once so re-opening Product Knowledge from its own entry point later
  // starts at Browse, not stuck on the last contextual target.
  useState(() => { if (initialContext) onInitialContextConsumed?.() })
  const [activeArea, setActiveArea] = useState(null)

  const { data: tree, loading: treeLoading, error: treeError, reload: reloadTree } = useAsyncData(() => pk.getTree(), [])

  const allAreas = (tree || []).flatMap(p => (p.areas || []).map(a => ({ ...a, product_key: p.product_key })))

  function openScreen(key) { setView({ mode: 'unit', key }) }
  function openWorkflow(key) { setView({ mode: 'workflow', key }) }
  function openItem(kind, key) { setView({ mode: 'item', kind, key }) }
  function openArea(areaKey) { setActiveArea(areaKey); setView({ mode: 'browse' }) }
  function backToBrowse() { setView({ mode: 'browse' }) }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.pageBg }}>
      <PageHeader
        title="Product Knowledge"
        subtitle="Browse and search how CostSpace's workflows and screens actually work. / تصفّح وابحث في طريقة عمل شاشات ووظائف CostSpace فعلياً."
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setView({ mode: 'glossary' })}
              style={{ fontSize: F.caption, fontWeight: 600, padding: '7px 14px', borderRadius: R.btn, border: `1px solid ${C.border}`, background: C.surface, color: C.ink2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              📖 Glossary / المصطلحات
            </button>
            <button onClick={() => setView({ mode: 'search' })}
              style={{ fontSize: F.caption, fontWeight: 600, padding: '7px 14px', borderRadius: R.btn, border: `1px solid ${C.border}`, background: C.surface, color: C.ink2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              🔍 Search / بحث
            </button>
          </div>
        }
        {...(view.mode !== 'browse' ? { onBack: backToBrowse, backLabel: 'Browse / تصفّح' } : {})}
      />

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {view.mode === 'search' && <ProductKnowledgeSearch onOpenScreen={openScreen} onOpenWorkflow={openWorkflow} onOpenItem={openItem} onOpenArea={openArea} onOpenProduct={backToBrowse} />}

        {view.mode === 'glossary' && <ProductKnowledgeGlossary onOpenItem={openItem} />}

        {view.mode === 'item' && <ProductKnowledgeItemReader kind={view.kind} itemKey={view.key} onOpenWorkflow={openWorkflow} onOpenScreen={openScreen} />}

        {view.mode === 'unit' && <ProductKnowledgeUnitReader unitKey={view.key} onOpenWorkflow={openWorkflow} onOpenScreen={openScreen} onOpenItem={openItem} />}

        {view.mode === 'workflow' && <ProductKnowledgeWorkflowReader workflowKey={view.key} onOpenScreen={openScreen} onOpenItem={openItem} />}

        {view.mode === 'browse' && (
          <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20, alignItems: 'start' }}>
            <div>
              <div style={{ fontSize: F.label, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                Areas / المناطق
              </div>
              {treeLoading && <LoadingRow label="Loading… / جارٍ التحميل…" />}
              {treeError && <ErrorRow error={treeError} onRetry={reloadTree} />}
              {!treeLoading && !treeError && (
                <AreaList areas={allAreas} activeKey={activeArea} onSelect={setActiveArea} />
              )}
            </div>
            <div>
              {activeArea
                ? <AreaContents areaKey={activeArea} onOpenScreen={openScreen} onOpenWorkflow={openWorkflow} />
                : <LoadingRow label="Select an area to browse its content. / اختر منطقة لتصفّح محتواها." />}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
